import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '#src/atoms/navigation/Box/index.tsx';
import { Card } from '#src/components/card/Card/index.tsx';
import { CardLockerOverlay } from '#src/components/card/CardLockerOverlay/index.tsx';
import { LoanWrapper } from '#src/components/card/Loan/index.tsx';
import { StrokedCard } from '#src/components/card/StrokedCard/index.tsx';
import { isType } from '#src/lib/gql/index.ts';

import type { PlayerAppearance_composeTeamBenchObjectInterface } from './__generated__/index.graphql.ts';

const PictureWrapper = styled(Vertical).attrs({ center: true })`
  aspect-ratio: var(--card-aspect-ratio);
  width: 100%; /** necessary for the aspect-ratio to work properly while loading the image */
  position: relative;
`;

const Picture = styled.div`
  width: 100%;
  border: 1.5px solid transparent;
`;

const CardWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
  transition: transform 200ms ease-in-out;
  justify-content: flex-start;
  &:not(:disabled) {
    &:hover,
    &:focus {
      transform: scale(1.05);
    }
  }
`;

const CardFooter = styled.footer`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

const PlayerPicture = styled.div`
  position: relative;
  width: 100%;
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-areas: 'topLeft topMiddle .';
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-50%);
`;
const TopLeft = styled.div`
  grid-area: topLeft;
  transform: translateX(calc(-1 * var(--half-unit)));
`;
const TopMiddle = styled.div`
  grid-area: topMiddle;
  display: inline-flex;
  justify-content: center;
`;
const BottomBadge = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(25%);
`;

/** Drag-related attributes for the card hit target (`<button>`). */
type PlayerAppearanceDragProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'draggable'
  | 'onDrag'
  | 'onDragCapture'
  | 'onDragEnd'
  | 'onDragEndCapture'
  | 'onDragEnter'
  | 'onDragEnterCapture'
  | 'onDragExit'
  | 'onDragExitCapture'
  | 'onDragLeave'
  | 'onDragLeaveCapture'
  | 'onDragOver'
  | 'onDragOverCapture'
  | 'onDragStart'
  | 'onDragStartCapture'
  | 'onDrop'
  | 'onDropCapture'
>;

type Props = {
  captain?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  appearance: PlayerAppearance_composeTeamBenchObjectInterface;
  score?: ReactNode;
  topBadge?: ReactNode;
  bottomBadge?: ReactNode;
  displayLocked?: boolean;
  dragProps?: PlayerAppearanceDragProps;
};

export const PlayerAppearance = ({
  appearance,
  disabled,
  onClick,
  captain,
  topBadge,
  bottomBadge,
  score,
  displayLocked,
  dragProps,
}: Props) => {
  const isDraggable = !!dragProps?.draggable;
  return (
    <LinkBox>
      <CardWrapper>
        <PictureWrapper>
          <StrokedCard active={!!captain} color="var(--c-rewards-pink)">
            <Picture>
              <LoanWrapper
                card={
                  isType(appearance, 'So5Appearance')
                    ? appearance.anyCard
                    : undefined
                }
              >
                <LinkOverlay
                  as="button"
                  onClick={onClick}
                  disabled={!onClick || disabled}
                  type="button"
                  {...dragProps}
                >
                  <PlayerPicture
                    style={isDraggable ? { cursor: 'grab' } : undefined}
                  >
                    <Card
                      card={appearance}
                      width={160}
                      draggable={isDraggable}
                    />
                    <CardLockerOverlay active={!!displayLocked} />
                  </PlayerPicture>
                </LinkOverlay>
              </LoanWrapper>
            </Picture>
          </StrokedCard>
          {!!bottomBadge && (
            <BottomBadge>
              <LinkOther as="div">{bottomBadge}</LinkOther>
            </BottomBadge>
          )}
        </PictureWrapper>
        {!!topBadge && (
          <Top>
            {!!captain && (
              <TopLeft>
                <LinkOther as="div">{captain}</LinkOther>
              </TopLeft>
            )}
            <TopMiddle>
              <LinkOther as="div">{topBadge}</LinkOther>
            </TopMiddle>
          </Top>
        )}
        <CardFooter>
          <LinkOther as="div">{score}</LinkOther>
        </CardFooter>
      </CardWrapper>
    </LinkBox>
  );
};

PlayerAppearance.fragments = {
  composeTeamBenchObjectInterface: gql`
    fragment PlayerAppearance_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      ... on So5Appearance {
        id
        anyCard {
          slug
          ...LoanWrapper_anyCard
        }
      }
      ...Card_composeTeamBenchObjectInterface
    }
    ${Card.fragments.composeTeamBenchObjectInterface}
    ${LoanWrapper.fragments.anyCard}
  ` as TypedDocumentNode<PlayerAppearance_composeTeamBenchObjectInterface>,
};
